import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '@/components/ui-custom/SEO';
import BlogSchema from '@/components/ui-custom/BlogSchema';
import { BLOG_POSTS } from '@/data/blogPosts';
import NotFound from './NotFound';

const BlogDetail = () => {
    const { slug } = useParams<{ slug: string }>();
    const post = BLOG_POSTS.find((p) => p.slug === slug);

    if (!post) return <NotFound />;

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': [
        { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://ardsdothan.com/' },
        { '@type': 'ListItem', 'position': 2, 'name': 'Blog', 'item': 'https://ardsdothan.com/blog' },
        { '@type': 'ListItem', 'position': 3, 'name': post.title, 'item': `https://ardsdothan.com/blog/${post.slug}` },
      ],
    };

    return (
        <>
            <SEO title={`${post.title} | Ard's Blog`} description={post.description} canonical={`/blog/${post.slug}`} additionalSchema={breadcrumbSchema} />
            <BlogSchema post={post} />

            <article className="min-h-screen bg-brand-white pt-32 pb-20 px-4">
                <div className="max-w-4xl mx-auto">
                    <Button asChild variant="ghost" className="mb-8 text-brand-navy hover:text-brand-coral gap-2">
                        <Link to="/blog"><ArrowLeft className="w-4 h-4" /> Back to Blog</Link>
                    </Button>

                    <header className="mb-12">
                        <div className="text-brand-coral font-bold uppercase tracking-widest mb-4">{post.category}</div>
                        <h1 className="font-heading font-black text-4xl lg:text-6xl text-brand-black mb-6 leading-tight">{post.title}</h1>
                        <div className="flex flex-wrap items-center gap-6 text-gray-500 border-y border-gray-100 py-6">
                            <span className="flex items-center gap-2">
                                <User className="w-4 h-4" /> {post.author}
                            </span>
                            <span className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" /> {post.date}
                            </span>
                            <span className="flex items-center gap-2">
                                <Clock className="w-4 h-4" /> {post.readTime}
                            </span>
                            <span className="flex items-center gap-2 text-brand-blue font-semibold">
                                <ShieldCheck className="w-4 h-4" /> Verified Expert Content
                            </span>
                        </div>
                    </header>

                    <div
                        className="prose prose-lg max-w-none text-gray-700"
                        dangerouslySetInnerHTML={{ __html: post.content }}
                    />
                </div>
            </article>
        </>
    );
};

export default BlogDetail;